/**
 * Canonical Google Visibility item state.
 *
 * Progress is derived from unique catalog items and their current status.
 * Completing an already-complete item is a no-op.
 */

import {
  ITEMS,
  ITEM_BY_ID,
  ITEM_STATUS,
  normalizeItemStatus,
} from "./catalog";

export const MAX_ITEM_NOTE_LENGTH = 280;

export function emptyItemRecord() {
  return {
    status: ITEM_STATUS.NOT_STARTED,
    note: "",
    evidence: {},
    updatedAt: null,
    updatedByProfileId: null,
  };
}

export function createEmptyItemStates() {
  return Object.fromEntries(ITEMS.map((item) => [item.id, emptyItemRecord()]));
}

export function sanitizeItemNote(note) {
  if (note == null) {
    return "";
  }
  return String(note).trim().slice(0, MAX_ITEM_NOTE_LENGTH);
}

function newerTimestamp(next, current) {
  if (!next) {
    return false;
  }
  if (!current) {
    return true;
  }
  return String(next) >= String(current);
}

/**
 * Merge persisted rows onto the catalog. Unknown keys are ignored.
 * Duplicate keys keep the newest updated_at.
 */
export function mergeCatalogWithStates(rows = []) {
  const byKey = {};
  for (const row of rows) {
    if (!row?.item_key || !ITEM_BY_ID[row.item_key]) {
      continue;
    }
    const incoming = {
      status: normalizeItemStatus(row.status),
      note: sanitizeItemNote(row.note),
      evidence: row.evidence && typeof row.evidence === "object" ? row.evidence : {},
      updatedAt: row.updated_at || null,
      updatedByProfileId: row.updated_by_profile_id || null,
    };
    const existing = byKey[row.item_key];
    if (!existing || newerTimestamp(incoming.updatedAt, existing.updatedAt)) {
      byKey[row.item_key] = incoming;
    }
  }

  const states = createEmptyItemStates();
  for (const item of ITEMS) {
    if (byKey[item.id]) {
      states[item.id] = byKey[item.id];
    }
  }
  return states;
}

export function applyItemState(states, itemId, { status, note } = {}) {
  if (!ITEM_BY_ID[itemId]) {
    return {
      states: states || createEmptyItemStates(),
      changed: false,
      reason: "unknown_item",
    };
  }

  const current = states?.[itemId] || emptyItemRecord();
  const nextStatus =
    status === undefined ? normalizeItemStatus(current.status) : normalizeItemStatus(status);
  const nextNote = note === undefined ? sanitizeItemNote(current.note) : sanitizeItemNote(note);

  if (current.status === nextStatus && sanitizeItemNote(current.note) === nextNote) {
    return {
      states: states || createEmptyItemStates(),
      changed: false,
      reason: nextStatus === ITEM_STATUS.COMPLETE ? "already_complete" : "unchanged",
    };
  }

  return {
    states: {
      ...(states || createEmptyItemStates()),
      [itemId]: {
        ...current,
        status: nextStatus,
        note: nextNote,
      },
    },
    changed: true,
    reason: "applied",
  };
}

/**
 * Keep local in-flight writes when a load snapshot returns before the save.
 */
export function overlayDirtyItemStates(loadedStates, localStates, dirtyItemIds) {
  const merged = { ...(loadedStates || createEmptyItemStates()) };
  if (!dirtyItemIds?.size) {
    return merged;
  }
  for (const itemId of dirtyItemIds) {
    if (!ITEM_BY_ID[itemId] || !localStates?.[itemId]) {
      continue;
    }
    merged[itemId] = localStates[itemId];
  }
  return merged;
}
